import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Routes, type EventName } from '@verifyloop/core';

/**
 * Live events.
 *
 * The wire format is plain Server-Sent Events: `event: <name>` + `data: <json>`,
 * but the transport here is `fetch` + a stream reader rather than the built-in
 * `EventSource`, for one unavoidable reason: `EventSource` cannot set an
 * `Authorization` header, and `GET /v1/events` is behind the same bearer key as
 * every other route. Reading the frames by hand costs thirty lines and keeps
 * auth, reconnection and the `?since=` replay cursor working.
 *
 * Everything else behaves as documented: named events, `hello` on connect,
 * automatic reconnection with backoff, and a missed-event replay so a dropped
 * connection costs a refetch rather than a silently wrong table.
 */

export const EVENT_NAMES: readonly EventName[] = [
  'run.created',
  'run.updated',
  'run.finished',
  'test.created',
  'test.updated',
  'project.updated',
  'schedule.fired',
  'schedule.paused',
  'tunnel.updated',
];

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'error';

export interface LiveEvent {
  event: EventName;
  data: Record<string, unknown>;
  at: string;
}

type Listener = (event: LiveEvent) => void;

interface EventsValue {
  status: StreamStatus;
  lastEventAt: string | null;
  eventCount: number;
  /** Announced to screen readers when a run reaches a verdict. */
  announcement: string | null;
  subscribe: (names: readonly EventName[], listener: Listener) => () => void;
}

const EventsContext = createContext<EventsValue | null>(null);

const MAX_BACKOFF_MS = 15_000;

function parseFrame(frame: string): LiveEvent | null {
  let name: string | null = null;
  const dataLines: string[] = [];

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (!line || line.startsWith(':')) continue; // keep-alive comment
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') name = value;
    else if (field === 'data') dataLines.push(value);
  }

  if (name === null) return null;
  const payload = dataLines.join('\n');
  let data: Record<string, unknown> = {};
  if (payload) {
    try {
      const parsed: unknown = JSON.parse(payload);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        data = parsed as Record<string, unknown>;
      }
    } catch {
      data = { raw: payload };
    }
  }
  // `hello` opens the stream and is not one of the domain events.
  return { event: name as EventName, data, at: new Date().toISOString() };
}

/** The handshake frame; it carries a cursor and is never dispatched. */
function isHello(event: LiveEvent): boolean {
  return (event.event as string) === 'hello';
}

export function EventsProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<StreamStatus>('idle');
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const [eventCount, setEventCount] = useState(0);
  const [announcement, setAnnouncement] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);

  const listeners = useRef(new Map<EventName, Set<Listener>>());
  const cursor = useRef<string | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);

  // The key lives in localStorage; watch storage events plus the auth value so
  // signing in/out tears the stream down without a page reload.
  useEffect(() => {
    const read = (): void => setApiKey(window.localStorage.getItem('verifyloop.apiKey'));
    read();
    window.addEventListener('storage', read);
    const timer = window.setInterval(read, 3_000);
    return () => {
      window.removeEventListener('storage', read);
      window.clearInterval(timer);
    };
  }, []);

  const subscribe = useCallback((names: readonly EventName[], listener: Listener) => {
    const registry = listeners.current;
    for (const name of names) {
      const set = registry.get(name) ?? new Set<Listener>();
      set.add(listener);
      registry.set(name, set);
    }
    return () => {
      for (const name of names) {
        const set = registry.get(name);
        if (!set) continue;
        set.delete(listener);
        if (set.size === 0) registry.delete(name);
      }
    };
  }, []);

  useEffect(() => {
    if (!apiKey) {
      setStatus('idle');
      return;
    }

    let stopped = false;
    let attempt = 0;
    let retryTimer: number | undefined;

    const dispatch = (live: LiveEvent): void => {
      if (isHello(live)) {
        const at = (live.data as { at?: string }).at;
        cursor.current = at && at !== '' ? at : cursor.current;
        return;
      }
      cursor.current = live.at;
      setLastEventAt(live.at);
      setEventCount((count) => count + 1);

      const set = listeners.current.get(live.event);
      if (set) for (const listener of set) listener(live);

      if (live.event === 'run.finished') {
        const runStatus = String((live.data as { status?: string }).status ?? '');
        if (runStatus) {
          setAnnouncement(`Run ${String(live.data['runId'] ?? '')} ${runStatus}`.trim());
        }
      }
    };

    const run = async (): Promise<void> => {
      while (!stopped) {
        const controller = new AbortController();
        abortRef.current = controller;
        setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
        try {
          const url = cursor.current
            ? `${Routes.events}?since=${encodeURIComponent(cursor.current)}`
            : Routes.events;
          const response = await fetch(url, {
            headers: { authorization: `Bearer ${apiKey}`, accept: 'text/event-stream' },
            signal: controller.signal,
          });
          if (response.status === 401 || response.status === 403) {
            setStatus('error');
            stopped = true;
            return;
          }
          if (!response.ok || !response.body) {
            throw new Error(`event stream rejected with ${response.status}`);
          }

          setStatus('open');
          attempt = 0;

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let boundary = buffer.indexOf('\n\n');
            while (boundary !== -1) {
              const frame = buffer.slice(0, boundary);
              buffer = buffer.slice(boundary + 2);
              const parsed = parseFrame(frame);
              if (parsed) dispatch(parsed);
              boundary = buffer.indexOf('\n\n');
            }
          }
          throw new Error('event stream closed by the server');
        } catch (error) {
          if (stopped) return;
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setStatus('reconnecting');
          attempt += 1;
          const delay = Math.min(1_000 * 2 ** (attempt - 1), MAX_BACKOFF_MS);
          await new Promise<void>((resolve) => {
            retryTimer = window.setTimeout(resolve, delay);
          });
        }
      }
    };

    void run();

    return () => {
      stopped = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      abortRef.current?.abort();
      abortRef.current = null;
      setStatus('idle');
    };
  }, [apiKey]);

  const value = useMemo<EventsValue>(
    () => ({ status, lastEventAt, eventCount, announcement, subscribe }),
    [status, lastEventAt, eventCount, announcement, subscribe],
  );

  return (
    <EventsContext.Provider value={value}>
      {children}
      <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        {announcement ?? ''}
      </div>
    </EventsContext.Provider>
  );
}

export function useEvents(): EventsValue {
  const value = useContext(EventsContext);
  if (!value) throw new Error('useEvents must be used inside <EventsProvider>');
  return value;
}

/**
 * Re-run `onEvent` whenever one of `names` arrives. The handler is held in a ref
 * so a component can pass an inline arrow function without re-subscribing on
 * every render.
 */
export function useLiveEvent(names: readonly EventName[], onEvent: (event: LiveEvent) => void): void {
  const { subscribe } = useEvents();
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    const listener: Listener = (event) => handler.current(event);
    return subscribe(names, listener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, names.join('|')]);
}